(() => {
  if (customElements.get('vehicle-finder')) return;

  const STEPS = ['make', 'model', 'year', 'drivetrain'];
  const LABELS = { make: 'MAKE', model: 'MODEL', year: 'YEAR', drivetrain: 'DRIVETRAIN' };
  const NOTES = {
    make: 'Vehicle brand', model: 'Vehicle model', year: 'Model year', drivetrain: 'Drive layout'
  };

  class VehicleFinder extends HTMLElement {
    async connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.events = new AbortController();
      this.dataReady = false;
      this.redirecting = false;
      this.selected = { make: null, model: null, year: null, drivetrain: null };
      this.activeStep = null;
      this.products = [];
      this.cacheElements();
      this.configureOverlap();
      this.bindEvents();
      await this.loadProducts();
      this.render();
    }

    disconnectedCallback() {
      this.events?.abort();
      this.loadController?.abort();
      this.initialized = false;
      if (this.ownsScrollLock) document.documentElement.classList.remove('lock-all');
      this.ownsScrollLock = false;
      if (this.backdrop) this.backdrop.hidden = true;
      this.hostSection?.classList.remove('vehicle-finder-host--overlap');
      this.overlapSource?.classList.remove('vehicle-finder-overlap-source');
      this.overlapSource?.style.removeProperty('--vf-promo-collision-shift');
      if (this.overlapResizeHandler) window.removeEventListener('resize', this.overlapResizeHandler);
      if (this.resizeHandler) window.removeEventListener('resize', this.resizeHandler);
      cancelAnimationFrame(this.resizeFrame);
    }

    configureOverlap() {
      this.hostSection = this.closest('.shopify-section');
      if (this.dataset.overlapEnabled !== 'true') return;
      const previous = this.hostSection?.previousElementSibling;
      if (!previous?.classList.contains('shopify-section')) return;
      this.overlapSource = previous;
      this.hostSection.classList.add('vehicle-finder-host--overlap');
      this.overlapSource.classList.add('vehicle-finder-overlap-source');
      const promotionActions = this.overlapSource.querySelector('.slideshow-promotion__actions');
      const promotionCopy = this.overlapSource.querySelector('.slideshow-promotion__copy');
      const finderPanel = this.querySelector('.vehicle-finder__panel');
      if (promotionActions && promotionCopy && finderPanel) {
        this.overlapResizeHandler = () => {
          this.overlapSource.style.setProperty('--vf-promo-collision-shift', '0px');
          requestAnimationFrame(() => {
            const actionBottom = promotionActions.getBoundingClientRect().bottom;
            const finderTop = finderPanel.getBoundingClientRect().top;
            const shift = Math.max(0, Math.ceil(actionBottom + 18 - finderTop));
            this.overlapSource.style.setProperty('--vf-promo-collision-shift', `${shift}px`);
          });
        };
        requestAnimationFrame(this.overlapResizeHandler);
        window.addEventListener('resize', this.overlapResizeHandler, { passive: true });
      }
    }

    cacheElements() {
      this.clearButton = this.querySelector('[data-vehicle-clear]');
      this.optionsPanel = this.querySelector('[data-vehicle-options]');
      this.optionsTitle = this.querySelector('[data-vehicle-options-title]');
      this.optionGrid = this.querySelector('[data-vehicle-option-grid]');
      this.summary = this.querySelector('[data-vehicle-summary]');
      this.progress = this.querySelector('[data-vehicle-progress]');
      this.progressCopy = this.querySelector('[data-vehicle-progress-copy]');
      this.loading = this.querySelector('[data-vehicle-loading]');
      this.loadMessage = this.querySelector('[data-vehicle-load-message]');
      this.retryButton = this.querySelector('[data-vehicle-retry]');
      this.results = this.querySelector('[data-vehicle-results]');
      this.resultCopy = this.querySelector('[data-vehicle-result-copy]');
      this.resultCount = this.querySelector('[data-vehicle-result-count]');
      this.productsContainer = this.querySelector('[data-vehicle-products]');
      this.sort = this.querySelector('[data-vehicle-sort]');
      this.findButton = this.querySelector('[data-vehicle-find]');
      this.empty = this.querySelector('[data-vehicle-empty]');
      this.backdrop = this.querySelector('[data-vehicle-backdrop]');
      this.sheetTitle = this.querySelector('[data-vehicle-sheet-title]');
      this.sheetOptions = this.querySelector('[data-vehicle-sheet-options]');
    }

    bindEvents() {
      const listen = (target, type, handler, options = {}) => target.addEventListener(type, handler, { ...options, signal: this.events.signal });
      listen(this.retryButton, 'click', () => this.loadProducts());
      listen(window, 'pageshow', () => { this.redirecting = false; this.render(); });
      this.querySelectorAll('[data-vehicle-step] .vehicle-finder__step-trigger').forEach((button) => {
        listen(button, 'click', () => {
          const step = button.closest('[data-vehicle-step]').dataset.vehicleStep;
          this.activeStep === step ? this.closeMenus() : this.openStep(step);
        });
      });
      listen(this.clearButton, 'click', () => this.reset());
      listen(this.querySelector('[data-vehicle-change]'), 'click', () => this.reset(true));
      listen(this.querySelector('[data-vehicle-sheet-close]'), 'click', () => this.closeMenus());
      listen(this.backdrop, 'click', (event) => { if (event.target === this.backdrop) this.closeMenus(); });
      listen(this.sort, 'change', () => this.renderProducts(this.matches || []));
      listen(this.findButton, 'click', () => {
        if (STEPS.some((step) => !this.selected[step])) return;
        this.matchProducts();
      });
      listen(this, 'keydown', (event) => { if (event.key === 'Escape') this.closeMenus();
        if (event.key === 'Tab' && !this.backdrop.hidden) {
          const controls = Array.from(this.backdrop.querySelectorAll('button:not(:disabled), a[href]'));
          const first = controls[0], last = controls[controls.length - 1];
          if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
          else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
        } });
      this.resizeHandler = () => {
        cancelAnimationFrame(this.resizeFrame);
        this.resizeFrame = requestAnimationFrame(() => this.closeMenus());
      };
      listen(window, 'resize', this.resizeHandler, { passive: true });
    }

    parsePage(root = this) {
      const script = root.querySelector('[data-vehicle-products-page]');
      if (!script) throw new Error('Missing vehicle data');
      const data = JSON.parse(script.textContent);
      if (!Array.isArray(data.products) || !Number.isInteger(data.page) || !Number.isInteger(data.pages) || data.page < 1 || data.pages < 0) throw new Error('Invalid vehicle data');
      return data;
    }

    async loadProducts() {
      this.loadController?.abort();
      const controller = new AbortController();
      this.loadController = controller;
      this.dataReady = false;
      this.loading.hidden = false;
      this.loadMessage.textContent = this.dataset.loadingText;
      this.retryButton.hidden = true;
      this.hideResults();
      this.render();
      try {
        const firstPage = this.parsePage();
        const products = [...firstPage.products];
        const pending = [];
        for (let page = 1; page <= firstPage.pages; page += 1) {
          if (page !== firstPage.page) pending.push(page);
        }
        for (let offset = 0; offset < pending.length; offset += 4) {
          const pages = await Promise.all(pending.slice(offset, offset + 4).map(page => this.fetchPage(page, firstPage, controller.signal)));
          pages.forEach(page => products.push(...page.products));
        }
        if (controller.signal.aborted || this.loadController !== controller) return;
        const unique = new Map();
        products.forEach(product => unique.set(String(product.id), this.normalizeProduct(product)));
        if (Number.isInteger(firstPage.total) && unique.size !== firstPage.total) throw new Error('Incomplete vehicle data');
        this.products = Array.from(unique.values());
        this.dataReady = true;
        this.loading.hidden = true;
      } catch (error) {
        if (controller.signal.aborted || this.loadController !== controller) return;
        controller.abort();
        this.products = [];
        this.loadMessage.textContent = this.dataset.errorText;
        this.retryButton.hidden = false;
      }
      if (this.isConnected && this.loadController === controller) this.render();
    }

    async fetchPage(page, firstPage, signal) {
      const request = new AbortController();
      const abort = () => request.abort();
      signal.addEventListener('abort', abort, { once: true });
      if (signal.aborted) request.abort();
      const timeout = window.setTimeout(abort, 15000);
      try {
        const url = new URL(window.location.href);
        url.searchParams.delete('sections');
        url.searchParams.set('section_id', this.dataset.sectionId);
        url.searchParams.set(firstPage.pageParam || 'page', String(page));
        const response = await fetch(url.toString(), { credentials: 'same-origin', signal: request.signal });
        if (!response.ok) throw new Error('Vehicle data request failed');
        const documentNode = new DOMParser().parseFromString(await response.text(), 'text/html');
        const data = this.parsePage(documentNode);
        if (data.page !== page || data.pages !== firstPage.pages || data.total !== firstPage.total) throw new Error('Vehicle data changed during loading');
        return data;
      } finally {
        window.clearTimeout(timeout);
        signal.removeEventListener('abort', abort);
      }
    }

    normalizeValues(value) {
      const raw = Array.isArray(value) ? value : value == null ? [] : [value];
      const values = raw.flatMap((item) => String(item).split(/[,;|]/));
      const unique = new Map();
      values.map((item) => item.trim()).filter(Boolean).forEach((item) => {
        const key = item.toLocaleLowerCase();
        if (!unique.has(key)) unique.set(key, item);
      });
      return Array.from(unique.values());
    }

    normalizeProduct(product) {
      return {
        ...product,
        make: this.normalizeValues(product.make),
        model: this.normalizeValues(product.model),
        year: this.normalizeValues(product.year),
        drivetrain: this.normalizeValues(product.drivetrain)
      };
    }

    equal(left, right) { return String(left).localeCompare(String(right), undefined, { sensitivity: 'base' }) === 0; }
    contains(product, key, value) { return !value || product[key].some((item) => this.equal(item, value)); }

    productsForStep(step) {
      const index = STEPS.indexOf(step);
      return this.products.filter((product) => STEPS.slice(0, index).every((key) => this.contains(product, key, this.selected[key])));
    }

    optionsFor(step) {
      const unique = new Map();
      this.productsForStep(step).forEach((product) => product[step].forEach((value) => {
        const key = value.toLocaleLowerCase();
        if (!unique.has(key)) unique.set(key, value);
      }));
      const values = Array.from(unique.values());
      if (step === 'year') return values.sort((a, b) => (Number(b) || 0) - (Number(a) || 0) || b.localeCompare(a));
      return values.sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }));
    }

    isMobile() { return window.matchMedia('(max-width: 740px)').matches; }

    render() {
      const chosen = STEPS.filter((step) => this.selected[step]);
      STEPS.forEach((step, index) => {
        const element = this.querySelector(`[data-vehicle-step="${step}"]`);
        const button = element.querySelector('.vehicle-finder__step-trigger');
        const enabled = this.dataReady && (index === 0 || Boolean(this.selected[STEPS[index - 1]]));
        element.classList.toggle('is-disabled', !enabled);
        element.classList.toggle('is-active', this.activeStep === step);
        element.classList.toggle('is-selected', Boolean(this.selected[step]));
        button.disabled = !enabled;
        button.setAttribute('aria-expanded', String(this.activeStep === step));
        element.querySelector('.vehicle-finder__step-value').textContent = this.selected[step] || `Choose ${step}`;
      });
      this.summary.querySelectorAll('.vehicle-finder__chip').forEach((chip) => chip.remove());
      this.summary.hidden = chosen.length === 0;
      chosen.forEach((step) => {
        const chip = document.createElement('span');
        chip.className = 'vehicle-finder__chip';
        const label = document.createElement('em'); label.textContent = LABELS[step];
        chip.append(label, document.createTextNode(this.selected[step]));
        this.summary.append(chip);
      });
      const isComplete = this.dataReady && chosen.length === STEPS.length;
      this.findButton.disabled = !isComplete || this.redirecting;
      this.findButton.classList.toggle('is-incomplete', !isComplete);
      this.findButton.setAttribute('aria-disabled', String(!isComplete));
      this.progress.style.width = `${chosen.length * 25}%`;
      this.progressCopy.textContent = ['Start with make', 'Next · select model', 'Next · select year', 'Next · select drivetrain', 'Ready · find fitment'][chosen.length];
    }

    buildOptions(step, target) {
      target.replaceChildren();
      this.optionsFor(step).forEach((value) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = `vehicle-finder__option${this.equal(this.selected[step], value) ? ' is-selected' : ''}`;
        const name = document.createElement('strong'); name.textContent = value;
        const note = document.createElement('span'); note.textContent = NOTES[step];
        button.append(name, note);
        button.addEventListener('click', () => this.choose(step, value));
        target.append(button);
      });
    }

    openStep(step) {
      const element = this.querySelector(`[data-vehicle-step="${step}"]`);
      if (element.classList.contains('is-disabled')) return;
      this.returnFocus = element.querySelector('.vehicle-finder__step-trigger');
      this.activeStep = step;
      this.render();
      if (this.isMobile()) {
        this.sheetTitle.textContent = `Select ${step}`;
        this.buildOptions(step, this.sheetOptions);
        this.backdrop.hidden = false;
        this.ownsScrollLock = !document.documentElement.classList.contains('lock-all');
        document.documentElement.classList.add('lock-all');
        this.querySelector('[data-vehicle-sheet-close]').focus();
      } else {
        this.optionsTitle.textContent = `Select ${step}`;
        this.buildOptions(step, this.optionGrid);
        this.optionsPanel.hidden = false;
      }
    }

    closeMenus() {
      this.activeStep = null;
      this.optionsPanel.hidden = true;
      this.backdrop.hidden = true;
      if (this.ownsScrollLock) document.documentElement.classList.remove('lock-all');
      this.ownsScrollLock = false;
      this.returnFocus?.focus({ preventScroll: true });
      this.returnFocus = null;
      this.render();
    }

    choose(step, value) {
      const index = STEPS.indexOf(step);
      this.selected[step] = value;
      STEPS.slice(index + 1).forEach((key) => { this.selected[key] = null; });
      this.hideResults();
      this.closeMenus();
      const next = STEPS[index + 1];
      if (next) this.openStep(next);
      else this.matchProducts();
    }

    hideResults() { this.results.hidden = true; this.empty.hidden = true; }

    matchProducts() {
      if (!this.dataReady || this.redirecting || STEPS.some(step => !this.selected[step])) return;
      this.hideResults();
      this.matches = this.products.filter((product) => STEPS.every((step) => this.contains(product, step, this.selected[step])));
      if (!this.matches.length) {
        this.results.hidden = true;
        this.empty.hidden = false;
        this.empty.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }
      if (this.matches.length === 1) {
        this.redirecting = true;
        this.render();
        window.location.assign(this.matches[0].url);
        return;
      }
      this.resultCopy.textContent = STEPS.map(step => this.selected[step]).join(' · ');
      this.resultCount.textContent = this.dataset.countText.replace('[count]', String(this.matches.length));
      this.renderProducts(this.matches);
      this.results.hidden = false;
      this.results.focus({ preventScroll: true });
      this.results.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
    }

    sortedProducts(products) {
      const sorted = [...products];
      if (this.sort.value === 'price-ascending') sorted.sort((a, b) => a.price - b.price);
      if (this.sort.value === 'price-descending') sorted.sort((a, b) => b.price - a.price);
      if (this.sort.value === 'alphabetical') sorted.sort((a, b) => a.title.localeCompare(b.title, undefined, { numeric: true, sensitivity: 'base' }));
      if (this.sort.value === 'featured') sorted.sort((a, b) => a.position - b.position);
      return sorted;
    }

    renderProducts(products) {
      this.productsContainer.replaceChildren();
      this.sortedProducts(products).forEach((product) => this.productsContainer.append(this.productCard(product)));
    }

    productCard(product) {
      const card = document.createElement('article'); card.className = 'vehicle-finder__product';
      const media = document.createElement('div'); media.className = 'vehicle-finder__product-media';
      if (product.image) { const image = document.createElement('img'); image.src = product.image; image.alt = product.title; image.loading = 'lazy'; image.decoding = 'async'; image.width = 600; image.height = 600; media.append(image); }
      const copy = document.createElement('div'); copy.className = 'vehicle-finder__product-copy';
      const badges = document.createElement('div'); badges.className = 'vehicle-finder__badges';
      const fitBadge = document.createElement('span'); fitBadge.className = 'vehicle-finder__badge'; fitBadge.textContent = 'DIRECT FIT'; badges.append(fitBadge);
      const onSale = product.compareAtPrice > product.price;
      if (onSale || !product.available) { const badge = document.createElement('span'); badge.className = `vehicle-finder__badge vehicle-finder__badge--${onSale ? 'sale' : 'sold-out'}`; badge.textContent = onSale ? 'SALE' : 'SOLD OUT'; badges.append(badge); }
      const title = document.createElement('h4'); title.className = 'vehicle-finder__product-title'; title.textContent = product.title;
      const fit = document.createElement('div'); fit.className = `vehicle-finder__fit${product.available ? '' : ' vehicle-finder__fit--unavailable'}`; fit.textContent = product.available ? '✓ FITMENT VERIFIED' : 'OUT OF STOCK';
      const prices = document.createElement('div'); prices.className = 'vehicle-finder__prices';
      const price = document.createElement('span'); price.className = `vehicle-finder__price${onSale ? ' vehicle-finder__price--sale' : ''}`; price.textContent = product.priceFormatted; prices.append(price);
      if (onSale) { const compare = document.createElement('s'); compare.className = 'vehicle-finder__compare'; compare.textContent = product.compareAtPriceFormatted; prices.append(compare); }
      const link = document.createElement('a'); link.className = 'vehicle-finder__view'; link.href = product.url; link.textContent = 'VIEW PRODUCT';
      copy.append(badges, title, fit, prices, link); card.append(media, copy); return card;
    }

    reset(scroll = false) {
      STEPS.forEach((step) => { this.selected[step] = null; });
      this.matches = [];
      this.hideResults();
      this.closeMenus();
      if (scroll) this.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  customElements.define('vehicle-finder', VehicleFinder);
})();
